<script setup lang="ts">
// Regulamento do Programa de Fidelidade renderizado como accordion único
// (abre/fecha o card inteiro), no mesmo formato do "Termos e Condições" do
// Indique e Ganhe (components/user/RefersTerms.vue) — é o MESMO componente do
// Figma, só com outro corpo de texto.
//
// Estado é local de propósito: `stores/` sincroniza com a base Cactus e não deve
// ser editado, e não há nada aqui que outro componente precise ler.
const isOpen = ref(false);

const toggle = () => {
  isOpen.value = !isOpen.value;
};
</script>

<template>
  <div class="lh-terms">
    <button
      :aria-expanded="isOpen"
      class="lh-terms__header"
      type="button"
      @click="toggle()"
    >
      <span class="lh-terms__icon-box">
        <NuxtIcon
          class="text-[20px]"
          filled
          name="user/refers-terms-info"
        />
      </span>

      <span class="lh-terms__title">
        Termos e Condições — Programa de Fidelidade Aposta Tudo
      </span>

      <NuxtIcon
        class="lh-terms__chevron"
        name="arrows/angle_down"
      />
    </button>

    <div
      v-show="isOpen"
      class="lh-terms__body"
    >
      <hr class="lh-terms__divider" />

      <h2 class="lh-terms__doc-title">
        Regulamento do Programa de Fidelidade - ApostaTudo
      </h2>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">1. Introdução</h3>
        <p>
          1.1 O Programa de Fidelidade é desenvolvido e mantido pela BLOW
          MARKETPLACE LTDA ("ApostaTudo"), empresa devidamente autorizada a
          operar a modalidade lotérica de apostas de quota fixa no Brasil,
          conforme Portaria SPA/MF nº 468, de 10 de março de 2025 (processo
          SIGAP nº 0039/2024).
        </p>
        <p>
          1.2 O Programa está alinhado à Lei nº 14.790/2023, à Portaria SPA/MF
          nº 1.231/2024 (com alterações da Portaria SPA/MF nº 2.217/2025) e à
          Política de Jogo Responsável da ApostaTudo, acessível em
          <a
            class="lh-terms__link"
            href="http://apostatudo.bet.br/user/account-protection"
            rel="noopener"
            target="_blank"
            >apostatudo.bet.br/user/account-protection</a
          >.
        </p>
        <p>
          1.3 O objetivo do Programa é reconhecer e recompensar a atividade dos
          usuários já cadastrados e ativos na plataforma, oferecendo uma
          experiência de entretenimento que combina fidelidade, diversão e
          responsabilidade.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">2. Termos de aceite</h3>
        <p>
          2.1 Ao aderir ao Programa, o usuário concorda com todas as disposições
          deste Regulamento, incluindo progressão de níveis, acúmulo e
          utilização de Coins, acesso à Loja de Recompensas e demais condições
          aqui descritas.
        </p>
        <p>
          2.2 A ApostaTudo reserva-se o direito de modificar, suspender ou
          encerrar, total ou parcialmente, o Programa de Fidelidade, incluindo o
          fornecimento de Coins, as regras de progressão, a disponibilidade de
          recompensas e os valores exigidos para resgate. Qualquer alteração
          relevante será comunicada com antecedência mínima de 24 (vinte e
          quatro) horas, por meio da plataforma. É responsabilidade do usuário
          acompanhar eventuais atualizações.
        </p>
        <p>
          2.3 Este Regulamento integra e é complementar aos Termos e Condições
          gerais da plataforma. Em caso de conflito, prevalecem as normas
          aplicáveis e a regulamentação da SPA/MF.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">3. Elegibilidade geral</h3>
        <p>
          3.1 A participação no Programa é restrita a usuários que,
          cumulativamente:
        </p>
        <p class="lh-terms__item">
          I — tenham concluído integralmente o cadastro na plataforma, incluindo
          a verificação de identidade (KYC) com reconhecimento facial e prova de
          vida (liveness);
        </p>
        <p class="lh-terms__item">
          II — tenham realizado, no mínimo, uma aposta na plataforma com saldo
          real próprio;
        </p>
        <p class="lh-terms__item">
          III — possuam conta ativa, verificada e em situação regular.
        </p>
        <p class="lh-terms__item">
          IV - Cumpram todos os requisitos legais para utilização das Casas de
          Apostas de Quotas Fixas em território nacional.
        </p>

        <p>3.2 É vedada a concessão de qualquer benefício do Programa:</p>
        <p class="lh-terms__item">
          I — antes da conclusão do cadastro e da verificação de identidade;
        </p>
        <p class="lh-terms__item">
          II — antes da realização de, pelo menos, uma aposta na plataforma;
        </p>
        <p class="lh-terms__item">
          III — como incentivo para cadastro ou para a realização da primeira
          aposta;
        </p>
        <p class="lh-terms__item">
          IV — condicionada a depósito ou a qualquer aporte financeiro.
        </p>

        <p>
          3.3 Não são elegíveis os usuários que se encontrem em uma ou mais das
          seguintes situações:
        </p>
        <p class="lh-terms__item">
          I — período de autoexclusão, temporária ou definitiva, interna ou
          centralizada (gov.br);
        </p>
        <p class="lh-terms__item">II — pausa temporária;</p>
        <p class="lh-terms__item">
          III — conta suspensa, bloqueada ou encerrada;
        </p>
        <p class="lh-terms__item">
          IV — cadastro incompleto ou pendente de verificação (KYC);
        </p>
        <p class="lh-terms__item">
          V — identificados como beneficiários de programas sociais ou de
          renegociação de dívida cuja participação em apostas de quota fixa seja
          vedada pela legislação vigente, incluindo:
        </p>
        <p class="lh-terms__subitem">
          a) beneficiários do Programa Bolsa Família e do Benefício de Prestação
          Continuada (BPC), nos termos do art. 8º, VIII, da Portaria SPA/MF nº
          1.231/2024, na redação dada pela Portaria SPA/MF nº 2.217/2025,
          regulamentada pela IN SPA/MF nº 22/2025 e prorrogada pela IN SPA/MF nº
          24/2025;
        </p>
        <p class="lh-terms__subitem">
          b) beneficiários do programa Novo Desenrola Brasil, nos termos do art.
          8º, IX, da Portaria SPA/MF nº 1.231/2024, incluído pela Portaria
          SPA/MF nº 1.237/2026 e regulamentado pela IN SPA/MF nº 3/2026;
        </p>
        <p class="lh-terms__subitem">
          c) beneficiários de renegociação de dívidas estudantis no âmbito do
          Fies, nos termos do art. 8º, X, da Portaria SPA/MF nº 1.231/2024,
          incluído pela Portaria SPA/MF nº 1.638/2026 e regulamentado pela IN
          SPA/MF nº 8/2026;
        </p>
        <p class="lh-terms__subitem">
          d) demais hipóteses de vedação que venham a ser incluídas no art. 8º
          da Portaria SPA/MF nº 1.231/2024 por norma superveniente.
        </p>
        <p class="lh-terms__item">VI — menores de 18 anos.</p>

        <p>
          3.4 Todo benefício concedido no âmbito do Programa constitui
          recompensa por atividade já realizada pelo usuário na plataforma, nos
          termos do art. 2º, XII, e do art. 42 da Portaria SPA/MF nº 1.231/2024.
          Em nenhuma hipótese a ApostaTudo concederá adiantamento, antecipação,
          bonificação ou vantagem prévia para a realização de apostas (art. 42,
          §1º, II).
        </p>
        <p>
          3.5 Para acessar qualquer funcionalidade do Programa, incluindo Loja,
          Missões, Torneios e Mini Games, o usuário precisa ter acumulado ao
          menos 1 (um) Coin em todo o seu histórico na plataforma.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">4. Coins — moeda do Programa</h3>
        <p>
          4.1 Coins são unidades de fidelidade exclusivas do Programa,
          atribuídas automaticamente ao usuário conforme sua atividade de
          apostas (turnover) na plataforma.
        </p>
        <p>
          4.2 Os Coins não são comprados com dinheiro, não são condicionados a
          depósito e não representam ativo financeiro nem investimento. São
          unidades de fidelidade sem valor monetário próprio, apresentadas em
          carteira separada da conta gráfica do usuário, nos termos do art. 43
          da Portaria SPA/MF nº 1.231/2024, utilizáveis exclusivamente na Loja
          de Recompensas para troca por itens e recompensas, incluindo saldo
          real, conforme o catálogo vigente.
        </p>
        <p>
          4.2.1 Quando a recompensa trocada na Loja for saldo real, o valor será
          imediatamente creditado na conta transacional do usuário, de que trata
          o art. 2º, V, da Portaria SPA/MF nº 615/2024, integrando-se ao saldo
          real do apostador para todos os efeitos. O saque desse valor segue as
          mesmas regras e o mesmo prazo aplicáveis a qualquer saque de saldo
          real na plataforma, isto é, em até 120 (cento e vinte) minutos, nos
          termos da Portaria SPA/MF nº 615/2024, sem necessidade de depósito ou
          qualquer outra condição adicional.
        </p>
        <p>
          4.3 Os Coins são pessoais e intransferíveis, estando vinculados à
          conta do usuário. Não podem ser transferidos a terceiros, vendidos,
          cedidos ou utilizados fora da plataforma.
        </p>
        <p>
          4.4 Os Coins servem exclusivamente para aquisição de itens na Loja de
          Recompensas. Os itens disponíveis incluem, conforme o catálogo
          vigente: Free Spins (rodadas grátis), itens de Mini Game, Freebets,
          Itens de Gema e Itens de Diamante.
        </p>
        <p>
          4.4.1 Os Itens de Gema e Itens de Diamante são recompensas de
          fidelidade adquiridas na Loja ou por meio de Mini Games, sempre em
          quantidade fixa e previamente conhecida pelo usuário. Exemplo: um baú
          adquirido por 10 Diamantes credita exatamente 5 Gemas. Os diferentes
          níveis de raridade têm caráter meramente visual e não alteram a
          quantidade nem o valor da recompensa entregue.
        </p>
        <p>
          4.4.2 Itens de Gema e Itens de Diamante não possuem valor monetário
          próprio nem podem ser sacados, transferidos ou utilizados fora da
          plataforma. Servem como moeda de troca na Loja de Recompensas por
          outros itens do catálogo vigente, incluindo saldo real, nos termos do
          item 4.4.3.
        </p>
        <p>
          4.4.3 Quando Itens de Gema ou Itens de Diamante forem trocados na Loja
          por saldo real, a taxa de conversão é fixa e informada ao usuário
          antes da confirmação, nos termos dos itens 12.4 e 4.2.1. Todo o
          percurso — obtenção do item e sua conversão — é determinístico: a
          quantidade de itens recebidos e o valor da conversão são fixos e
          conhecidos previamente. O nível de raridade é apenas visual e não
          influencia a quantidade recebida nem o valor de conversão. Não há, em
          nenhuma etapa, sorteio, aleatoriedade de valor ou resultado incerto.
        </p>
        <p>
          4.5 Validade: os Coins expiram no último dia de cada mês, sendo
          zerados no início do mês seguinte. A ApostaTudo comunicará a
          proximidade da expiração aos usuários por meio dos canais oficiais
          antes do zeramento.
        </p>
        <p>
          4.6 A concessão de Coins ocorre automaticamente após a conclusão de
          atividades qualificadas. O saldo atualizado está sempre disponível
          para consulta na plataforma.
        </p>
        <p>
          4.7 A ApostaTudo poderá ajustar, revogar ou modificar as regras de
          concessão, os valores de troca e o saldo de Coins, mediante aviso
          prévio de, no mínimo, 24 (vinte e quatro) horas, por meio da
          plataforma. Alterações não afetam trocas já concluídas.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">5. Classificação VIP</h3>
        <p>
          5.1 O Programa possui uma estrutura de classificação interna que
          reconhece o histórico de atividade do usuário na plataforma. A
          classificação é atribuída pela ApostaTudo com base em critérios
          cumulativos de atividade já realizada — não constitui incentivo para
          novos depósitos ou apostas.
        </p>
        <p>
          5.2 A classificação é permanente. Uma vez atribuída, o usuário não
          perde a categoria por inatividade. A remoção da classificação ocorre
          apenas por violação dos Termos e Condições gerais (fraude, arbitragem,
          contas múltiplas) ou por autoexclusão.
        </p>
        <p>
          5.3 Usuários com classificação VIP poderão ter acesso a benefícios
          diferenciados, incluindo cashbacks com porcentagens maiores,
          atendimento em canal exclusivo e auditável, missões exclusivas e
          torneios exclusivos, conforme as condições divulgadas na plataforma.
        </p>
        <p>
          5.4 A ApostaTudo poderá criar novas categorias, ajustar os critérios
          de classificação ou alterar os benefícios associados, mediante
          comunicação prévia na plataforma. Alterações não afetam a
          classificação já atribuída antes da mudança.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">6. Benefícios do Programa</h3>
        <p>
          6.1 O Programa oferece os seguintes benefícios, conforme o nível do
          usuário e as condições vigentes:
        </p>
        <ol class="lh-terms__ordered">
          <li>
            Cashback: benefício calculado com base na atividade do usuário nos
            jogos elegíveis durante o período (Seção 7).
          </li>
          <li>
            Missões: tarefas periódicas com metas e recompensas (Seção 8).
          </li>
          <li>Torneios: competições com ranking e premiação (Seção 9).</li>
          <li>Mini Games: jogos interativos com recompensas (Seção 10).</li>
          <li>
            Loja de Recompensas: ambiente para troca de Coins por itens e
            recompensas (Seção 12).
          </li>
        </ol>
        <p>
          6.2 Vantagens por nível: usuários em níveis mais altos do Programa
          poderão ter acesso a cashbacks maiores, atendimento em canal
          exclusivo, missões exclusivas e torneios exclusivos, conforme as
          condições divulgadas na plataforma.
        </p>
        <p>
          6.3 Os benefícios, recompensas e condições estão sujeitos a
          disponibilidade e podem ser modificados, substituídos ou removidos
          mediante comunicação prévia na plataforma. Alterações não afetam
          benefícios já creditados antes da mudança.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">7. Cashback</h3>
        <p>
          7.1 O Cashback é um benefício calculado com base na atividade do
          usuário nos jogos elegíveis durante o período de apuração. O valor é
          creditado automaticamente e deve ser resgatado pelo usuário.
        </p>

        <p>7.1.1 Cashback Diário — Slots</p>
        <p class="lh-terms__item">
          a) Válido exclusivamente para os jogos de Slots listados como
          elegíveis na página da promoção:
          apostatudo.bet.br/promotions/cashback-diario-ativo
        </p>
        <p class="lh-terms__item">
          b) Período de apuração: 24 horas anteriores, das 00h00 às 23h59
          (horário de Brasília).
        </p>
        <p class="lh-terms__item">
          c) Percentual escalonado por faixa de atividade, conforme tabela
          publicada na página da promoção, variando de 2% a 20%, com teto máximo
          de R$ 5.000,00 por dia.
        </p>
        <p class="lh-terms__item">
          d) O benefício é liberado a partir das 12h00 do dia seguinte ao
          período de apuração.
        </p>
        <p class="lh-terms__item">
          e) Prazo para resgate: 24 horas após a liberação. Benefício não
          resgatado no prazo expira automaticamente.
        </p>

        <p>7.1.2 Cashback Diário — Crash</p>
        <p class="lh-terms__item">
          a) Válido exclusivamente para os jogos de Crash listados como
          elegíveis na página da promoção:
          apostatudo.bet.br/promotions/cashback-aviator
        </p>
        <p class="lh-terms__item">
          b) Período de apuração, percentuais, teto, liberação e prazo de
          resgate seguem as mesmas regras do Cashback Diário de Slots (cláusula
          7.1.1, itens "b" a "e").
        </p>

        <p>7.1.3 Regras gerais de Cashback</p>
        <p class="lh-terms__item">
          a) O Cashback é concedido em saldo real e pode ser sacado ou utilizado
          na plataforma, sem condição de novo depósito.
        </p>
        <p class="lh-terms__item">
          b) As faixas, percentuais, jogos elegíveis e demais condições são os
          publicados na respectiva página da promoção no momento da apuração.
        </p>
        <p class="lh-terms__item">
          c) O Cashback não é válido para apostas realizadas com saldo bônus,
          freebets ou qualquer outro saldo promocional.
        </p>
        <p class="lh-terms__item">
          d) Usuários em autoexclusão ou pausa temporária não recebem Cashback
          durante o período de restrição. Na hipótese de autoexclusão, o
          Cashback já apurado sobre saldo real, quando devido, será
          disponibilizado exclusivamente para saque.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">8. Missões</h3>
        <p>
          8.1 As Missões são tarefas periódicas disponibilizadas no Programa,
          com metas específicas e recompensas definidas para cada uma. Ao
          concluir uma Missão, o usuário recebe a recompensa descrita (Coins,
          Free Spins, Freebets ou outros benefícios).
        </p>
        <p>8.2 As Missões são organizadas nas seguintes categorias:</p>
        <ol class="lh-terms__ordered">
          <li>
            Missões Básicas: disponíveis para todos os participantes,
            independentemente do nível.
          </li>
          <li>
            Missões de Entrada: atividades iniciais para familiarizar o usuário
            com o Programa (ex.: primeira troca na Loja, trocar avatar, abrir
            primeiro Mini Game).
          </li>
          <li>
            Missões Temáticas: eventos sazonais ou temporários ligados a datas
            comemorativas (ex.: Missões de Páscoa, Carnaval).
          </li>
          <li>
            Missões de Satisfação: recompensas por responder pesquisas de
            feedback sobre a plataforma.
          </li>
          <li>
            Missões por Nível: tarefas cuja dificuldade é ajustada conforme o
            nível do usuário no Programa.
          </li>
        </ol>
        <p>
          8.3 Cada Missão possui período de início e fim, metas, recompensas e
          regras próprias, informadas na plataforma antes da participação.
        </p>
        <p>
          8.4 As Missões e suas recompensas podem ser alteradas, adicionadas ou
          removidas pela ApostaTudo, mediante comunicação prévia na plataforma.
        </p>
        <p>
          8.5 Após a conclusão dos requisitos de uma Missão, o benefício
          correspondente não é creditado automaticamente: o usuário deve acessar
          a Missão concluída e clicar em "Resgatar" para receber a recompensa em
          sua conta. O resgate observa as regras da Seção 13, inclusive quanto
          ao prazo e forma de recebimento.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">9. Torneios</h3>
        <p>
          9.1 Os Torneios são competições promovidas dentro do Programa, com
          ranking, período definido e premiação, com o objetivo de incentivar o
          engajamento dos participantes.
        </p>
        <p>
          9.2 A pontuação nos Torneios será calculada com base em ganhos
          líquidos obtidos pelo usuário nos jogos elegíveis durante o período do
          torneio (ganhos menos perdas apurados no período), ou por rodadas
          vencidas.
        </p>
        <p>
          9.3 A participação nos Torneios é automática e não é necessária
          inscrição manual, salvo se as regras específicas de um torneio
          dispuserem de forma diversa.
        </p>
        <p>
          9.4 Os prêmios dos Torneios podem ser concedidos em Coins, freespins,
          freebet ou em saldo real, conforme especificado nas regras de cada
          torneio. Prêmios em saldo real podem ser sacados ou utilizados na
          plataforma, sem condição de novo depósito.
        </p>
        <p>
          9.5 Somente apostas realizadas com saldo real contam para a pontuação
          dos Torneios, salvo indicação expressa em contrário nas regras
          específicas.
        </p>
        <p>
          9.6 As regras específicas de cada Torneio (jogos elegíveis, período,
          tabela de premiação, métricas de pontuação) serão publicadas na página
          do Torneio antes de seu início.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">10. Mini Games</h3>
        <p>
          10.1 Os Mini Games são jogos interativos do Programa, disponíveis em
          formatos como Roletas, Raspadinhas, Caixas Misteriosas, Baús e outros
          modelos.
        </p>
        <p>
          10.2 As recompensas dos Mini Games podem incluir Coins, Free Spins,
          itens e outros benefícios do Programa.
        </p>
        <p>
          10.3 Quando a recompensa de um Mini Game for saldo real, a entrega
          será sempre determinística: o usuário saberá o valor exato que
          receberá caso seja contemplado antes de confirmar a participação.
        </p>
        <p>
          10.4 Os Mini Games não constituem aposta nem distribuição de prêmio em
          dinheiro. São recursos de gamificação e entretenimento do Programa.
        </p>
        <p>
          10.5 A disponibilidade, os formatos e as recompensas dos Mini Games
          podem variar conforme campanhas e períodos, sendo informados na
          plataforma.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">
          11. Dinâmicas e ações especiais
        </h3>
        <p>
          11.1 A ApostaTudo poderá criar, a qualquer tempo, dinâmicas e ações
          especiais no âmbito do Programa, como rankings, desafios, competições
          interativas e outras mecânicas de engajamento.
        </p>
        <p>
          11.2 Cada dinâmica ou ação especial terá regras, período, formato de
          premiação e condições próprias, que serão informados na plataforma
          antes do início e antes da participação.
        </p>
        <p>
          11.3 As dinâmicas e ações especiais observarão as mesmas regras de
          elegibilidade (Seção 3), resgate (Seção 13) e jogo responsável (Seção
          18) deste Regulamento, bem como, no que couber, as seguintes
          salvaguardas:
        </p>
        <p class="lh-terms__item">
          a) quando a dinâmica ou ação especial distribuir saldo real, aplica-se
          a regra de determinismo do item 10.3, ou seja, haverá a determinação
          de uma quota fixa, portanto a entrega de saldo real deve ser sempre
          determinística e previamente conhecida pelo usuário antes de sua
          confirmação ou participação.
        </p>
        <p>
          11.4 A ApostaTudo poderá alterar, suspender ou encerrar qualquer
          dinâmica ou ação especial, mediante comunicação prévia na plataforma.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">12. Loja de Recompensas</h3>
        <p>
          12.1 A Loja de Recompensas é o ambiente dentro da plataforma onde o
          usuário pode trocar seus Coins por itens e recompensas.
        </p>
        <p>
          12.2 Os itens disponíveis na Loja incluem, conforme o catálogo vigente
          e sujeitos a atualização mediante comunicação prévia (item 12.5): Free
          Spins (rodadas grátis), saldo real (nos termos do item 4.2.1),
          Freebets, itens de Mini Game, Itens de Gema e Itens de Diamante (nos
          termos do item 4.4), e demais itens que venham a ser incluídos no
          catálogo, os quais somente poderão ser disponibilizados ao usuário
          após descrição completa de sua natureza, mecânica e condições de uso
          nos termos deste Regulamento ou de Termos e Condições específicos,
          previamente à sua oferta na plataforma.
        </p>
        <p>
          12.3 O custo em Coins de cada item é fixo e informado na Loja antes da
          confirmação. Uma vez confirmada a troca, o débito de Coins e o crédito
          do item são imediatos e irretratáveis.
        </p>
        <p>
          12.4 Quando a recompensa for saldo real, a taxa de conversão será fixa
          e informada na Loja antes da confirmação. O saldo real creditado
          poderá ser sacado ou utilizado na plataforma, sem condição de novo
          depósito.
        </p>
        <p>
          12.4.1 Quando a obtenção do saldo real ocorrer por meio de etapas
          intermediárias — como a conversão de Coins ou Diamantes em Gemas e a
          posterior conversão de Gemas em saldo real — todas as etapas serão
          realizadas a taxas fixas, conhecidas e informadas ao usuário antes de
          cada confirmação. Em nenhuma etapa haverá sorteio, aleatoriedade ou
          variação de valor.
        </p>
        <p>
          12.5 Os itens e recompensas da Loja estão sujeitos a disponibilidade
          de catálogo e podem ser modificados, substituídos ou removidos a
          qualquer tempo, mediante comunicação prévia na plataforma. Alterações
          não afetam trocas já concluídas.
        </p>
        <p>
          12.6 Cada item trocado poderá ter regras próprias de uso, validade ou
          limitação, informadas na descrição do produto antes da troca. Quando o
          item possuir Termos e Condições específicos, estes prevalecem no que
          dispuserem de forma diversa, observado que nenhum item poderá ser
          disponibilizado na Loja sem que sua mecânica, condições e eventuais
          Termos específicos estejam previamente documentados e acessíveis ao
          usuário, nos termos do item 12.2.
        </p>
        <p>
          12.6.1 A título de exemplo, aplicam-se Termos e Condições específicos
          aos Freebets (item 8.1) e aos Itens de Gema e Itens de Diamante (item
          4.4).
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">13. Resgate de Benefícios</h3>
        <p>
          13.1 Canal exclusivo de resgate. Todo benefício, bônus, prêmio,
          cashback, freebet, item ou recompensa do Programa deve ser resgatado
          pelo usuário exclusivamente na aba "Prêmios e Recompensas" do menu
          "Meu Perfil" na plataforma.
        </p>
        <p>
          13.2 Prazo de resgate. Cada benefício ou recompensa terá prazo de 24
          (vinte e quatro) horas para resgate, salvo se as condições específicas
          dispuserem de forma diversa. Decorrido o prazo sem resgate, o
          benefício expira automaticamente, sem direito a compensação,
          substituição ou ressarcimento.
        </p>
        <p>
          13.3 Pausa temporária. Caso o usuário solicite pausa temporária
          enquanto houver benefício pendente, o prazo de 24 horas continuará
          correndo normalmente. Considerando que o retorno da pausa somente
          ocorre após 24 horas de sua solicitação, o usuário poderá não ter
          acesso à plataforma dentro do prazo de resgate, hipótese em que os
          benefícios expirarão automaticamente.
        </p>
        <p>
          13.4 Autoexclusão. Na hipótese de autoexclusão, o usuário perderá o
          direito de resgatar quaisquer benefícios promocionais pendentes.
          Exceção: valores de Cashback apurados sobre saldo real, quando
          devidos, serão disponibilizados exclusivamente para saque,
          permanecendo vedada qualquer operação de jogo durante a autoexclusão.
        </p>
        <p>
          13.5 Acompanhamento. O usuário pode visualizar suas ofertas na aba
          "Prêmios e Recompensas", identificadas como Ativas, Programadas, Em
          processamento ou Expiradas. É dever exclusivo do usuário: (i)
          verificar periodicamente as ofertas e seus prazos; (ii) tomar ciência
          de limitações, restrições e regras de elegibilidade; (iii) executar o
          resgate dentro do prazo; e (iv) manter dados de contato atualizados.
        </p>
        <p>
          13.6 Irretratabilidade. Concluído o resgate: (i) o ato é irretratável
          e irrevogável; (ii) o benefício é pessoal e intransferível; e (iii) o
          uso fica condicionado a este Regulamento e às regras específicas da
          promoção.
        </p>
        <p>
          13.7 Prevalência de registros. Em qualquer divergência, prevalecerão
          os registros do banco de dados, logs e trilhas de auditoria da
          plataforma (incluindo data/hora de disponibilização, aceite e
          resgate), salvo prova em contrário apresentada pelo usuário. Capturas
          de tela, e-mails, mensagens e materiais promocionais poderão ser
          apresentados pelo usuário como elementos de prova e serão analisados
          pela ApostaTudo em conjunto com os registros oficiais da plataforma
          para a resolução da divergência.
        </p>
        <p>
          13.8 Auditoria. A ApostaTudo poderá auditar, suspender, reter, anular
          ou reverter benefícios e ganhos quando identificar indícios de fraude,
          abuso, uso automatizado, contas múltiplas, conluio, erro material ou
          violação deste Regulamento e/ou dos Termos e Condições gerais, nos
          termos das Cláusulas 5.3 e 5.4 dos Termos e Condições gerais.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">
          14. Notificações e comunicações
        </h3>
        <p>
          14.1 A ApostaTudo poderá comunicar informações sobre o Programa por
          meio de e-mail, SMS, WhatsApp, notificações push, pop-ups e mensagens
          inbox na plataforma.
        </p>
        <p>
          14.2 As comunicações possuem caráter informativo e não substituem o
          dever de acompanhamento pelo usuário (cláusula 13.5). Atrasos de
          entrega de mensagens por terceiros (operadoras, provedores, apps) não
          prorrogam prazos de resgate.
        </p>
        <p>
          14.3 Para fins de prazos, prevalece o horário do servidor da
          plataforma (GMT-3, Brasília).
        </p>
        <p>
          14.4 O usuário poderá, a qualquer momento e de forma gratuita,
          cancelar o recebimento de comunicações promocionais, sem que isso
          afete a manutenção de sua conta ou a participação no Programa,
          bastando acessar a aba "Dados da Conta" na plataforma e ajustar suas
          preferências, ou solicitar pelo e-mail suporte@apostatudo.bet.br. O
          cancelamento de comunicações promocionais não afeta o recebimento de
          comunicações essenciais (conta, transações, obrigações legais e de
          jogo responsável).
        </p>
        <p>
          14.5 Não serão enviadas comunicações promocionais a usuários em
          período de autoexclusão, pausa temporária ou com conta suspensa.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">
          15. Segurança, auditoria e prevenção a fraudes
        </h3>
        <p>
          15.1 A ApostaTudo poderá realizar verificações de segurança,
          auditorias e procedimentos de identidade (KYC) a qualquer momento.
        </p>
        <p>
          15.2 É proibido: criar múltiplas contas; usar automações, scripts ou
          mecanismos de exploração do sistema; manipular resultados ou explorar
          falhas técnicas; transferir benefícios entre contas; ou obter
          vantagens indevidas por qualquer meio.
        </p>
        <p>
          15.3 Diante de indícios concretos de irregularidade, a ApostaTudo
          poderá suspender preventivamente a participação e, após análise,
          cancelar transações, reverter saldos concedidos indevidamente,
          suspender ou excluir Coins e bloquear a conta, nos termos das
          Cláusulas 5.3 e 5.4 dos Termos e Condições gerais. Antes de decisão
          definitiva que implique perda de Coins ou saldo, o usuário será
          notificado e poderá apresentar esclarecimentos, salvo quando houver
          obrigação legal em sentido diverso.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">
          16. Tratamento de dados pessoais
        </h3>
        <p>
          16.1 Ao aderir ao Programa, o usuário declara estar ciente e de acordo
          com a coleta, uso, armazenamento, tratamento e compartilhamento de
          seus dados pessoais, nos termos da Lei nº 13.709/2018 (LGPD),
          exclusivamente para finalidades relacionadas à sua participação no
          Programa.
        </p>
        <p>
          16.2 O tratamento será realizado de forma adequada, pertinente e
          limitada ao mínimo necessário, em conformidade com os princípios da
          finalidade, necessidade, segurança e transparência.
        </p>
        <p>
          16.3 Os dados poderão incluir: nome, CPF, e-mail, telefone, data de
          nascimento, dados de navegação e consumo, e informações sobre a
          participação no Programa.
        </p>
        <p>
          16.4 O compartilhamento de dados pessoais com parceiros comerciais e
          prestadores de serviço poderá ocorrer nas seguintes hipóteses e bases
          legais, nos termos da Lei nº 13.709/2018 (LGPD):
        </p>
        <p class="lh-terms__item">
          a) Atribuição e resgate de recompensas e operacionalização e suporte
          técnico do Programa: compartilhamento com prestadores de serviço
          estritamente necessários à execução do Programa (ex.: processadores de
          pagamento, provedores de infraestrutura), com base na execução do
          contrato (art. 7º, V, LGPD), limitado aos dados necessários para essa
          finalidade específica.
        </p>
        <p class="lh-terms__item">
          b) Cumprimento de obrigações legais ou regulatórias: compartilhamento
          com autoridades e órgãos competentes, com base no cumprimento de
          obrigação legal ou regulatória (art. 7º, II, LGPD), na exata medida
          exigida pela norma aplicável.
        </p>
        <p class="lh-terms__item">
          c) Execução de campanhas de marketing e divulgação com parceiros
          comerciais: compartilhamento condicionado a consentimento específico,
          prévio, informado e destacado do usuário (art. 7º, I, e art. 8º, §4º,
          LGPD), obtido separadamente da aceitação deste Regulamento, podendo
          ser revogado a qualquer tempo pelo usuário sem prejuízo de sua
          participação no Programa, conforme o item 16.5.
        </p>
        <p>
          16.5 O usuário poderá, a qualquer tempo, exercer seus direitos
          previstos na LGPD (acesso, correção, eliminação, portabilidade,
          revogação do consentimento), pelo canal
          <a
            class="lh-terms__link"
            href="mailto:dpo@apostatudo.bet.br"
            >dpo@apostatudo.bet.br</a
          >.
        </p>
        <p>
          16.6 As disposições deste capítulo complementam a Política de
          Privacidade da plataforma.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">
          17. Responsabilidade e limitações
        </h3>
        <p>
          17.1 A ApostaTudo empregará esforços para manter o Programa disponível
          e funcional. Não se responsabiliza por eventos de força maior ou fato
          exclusivo de terceiro; contudo, caso uma falha da própria plataforma
          resulte na perda de item ou Coins regularmente obtidos, a ApostaTudo
          os restabelecerá.
        </p>
        <p>
          17.2 Os jogos de aposta disponíveis na plataforma (como slots, jogos
          crash e cassino ao vivo) baseiam-se em resultados aleatórios gerados
          por sistemas auditados e certificados. Não há garantia de ganhos.
        </p>
        <p>
          17.2.1 Os Mini Games são recursos de gamificação e entretenimento, não
          constituem aposta e entregam recompensas em quantidade fixa e
          previamente conhecida pelo usuário (Coins, Free Spins e Itens de Gema
          ou de Diamante). Eventuais elementos visuais de aleatoriedade são
          meramente ilustrativos e não alteram a recompensa, que é sempre
          determinística. Os Mini Games não distribuem saldo real nem quantidade
          variável de itens por sorteio.
        </p>
        <p>
          17.3 A utilização de qualquer item ou recompensa do Programa não
          constitui promessa de lucro, investimento nem garantia de retorno.
        </p>
        <p>
          17.4 Em caso de falha técnica que impeça ou prejudique a participação,
          aplica-se o disposto na Cláusula 12.1 dos Termos e Condições gerais da
          plataforma.
        </p>
        <p>
          17.5 Situações não previstas neste Regulamento serão analisadas pela
          ApostaTudo e comunicadas ao usuário, assegurado o direito de
          reclamação pelos canais de atendimento e pela Ouvidoria (<a
            class="lh-terms__link"
            href="mailto:ouvidoria@apostatudo.bet.br"
            >ouvidoria@apostatudo.bet.br</a
          >).
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">18. Jogo Responsável</h3>
        <p>
          18.1 A ApostaTudo promove o jogo responsável. Apostar pode causar
          dependência e levar à perda de dinheiro. Aposta não é investimento.
          Jogue com responsabilidade.
        </p>
        <p>
          18.2 O usuário tem à disposição ferramentas de limites (depósito,
          aposta, perda e tempo de sessão), pausa temporária e autoexclusão,
          acessíveis em
          <a
            class="lh-terms__link"
            href="http://apostatudo.bet.br/user/account-protection"
            rel="noopener"
            target="_blank"
            >apostatudo.bet.br/user/account-protection</a
          >.
        </p>
        <p>
          18.3 O usuário pode realizar a autoexclusão centralizada (todas as
          casas de aposta) pelo portal gov.br/fazenda (Secretaria de Prêmios e
          Apostas &gt; Autoexclusão), nos termos da Portaria SPA/MF nº
          2.579/2025.
        </p>
        <p>
          18.4 Usuários em autoexclusão ou pausa temporária não participam do
          Programa, não acumulam Coins, não acessam a Loja, Missões, Torneios ou
          Mini Games, e não recebem comunicações promocionais durante o período
          de restrição.
        </p>
        <p>
          18.5 Em caso de dificuldades com o jogo, o usuário pode acessar apoio
          gratuito e sigiloso pelo aplicativo Meu SUS Digital (Miniapps &gt;
          "Problemas com jogos de apostas?") ou ligar para o CVV (188).
        </p>
        <p>18.6 Proibido para menores de 18 anos.</p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">19. Natureza da participação</h3>
        <p>
          19.1 A participação no Programa não gera qualquer custo ou ônus
          adicional ao usuário. A participação decorre da própria utilização
          regular dos serviços da plataforma.
        </p>
        <p>
          19.2 Os itens, Coins e recompensas são pessoais e intransferíveis, não
          podendo ser vendidos, cedidos ou utilizados fora da plataforma.
        </p>
      </section>

      <section class="lh-terms__section">
        <h3 class="lh-terms__section-title">20. Disposições finais</h3>
        <p>
          20.1 Este Regulamento entra em vigor na data de sua publicação na
          plataforma e substitui qualquer versão anterior.
        </p>
        <p>
          20.2 A ApostaTudo poderá atualizar este Regulamento a qualquer
          momento, comunicando alterações materiais com antecedência mínima de
          24 horas. A versão vigente será sempre a publicada na plataforma.
        </p>
        <p>
          20.3 Este Regulamento integra e é complementar aos Termos e Condições
          gerais, à Política de Privacidade e à Política de Jogo Responsável da
          ApostaTudo. Em caso de conflito, prevalecem as normas aplicáveis e a
          regulamentação da SPA/MF.
        </p>
        <p>
          20.4 BLOW MARKETPLACE LTDA · CNPJ 37.486.405/0001-91 · Autorização
          SPA/MF nº 468, de 10 de março de 2025 · apostatudo.bet.br
        </p>
      </section>
    </div>
  </div>
</template>

<style scoped lang="scss">
// Medidas do node mobile do Figma (21448:65821), que é o componente em escala
// 1:1 — o node desktop (21451:68108) é o MESMO card exportado a 95% (12 →
// 11,403 / 16 → 15,204), então não há breakpoint: um só tamanho serve os dois.
// O corpo do texto vem do node desktop; o mobile ainda está com a cópia do
// Indique e Ganhe (sobra do componente duplicado no Figma).
.lh-terms {
  @apply w-full overflow-hidden rounded-[12px] bg-white/10 p-[10px];

  backdrop-filter: blur(5px);

  &__header {
    @apply flex w-full cursor-pointer select-none items-center gap-2 bg-transparent text-left;
  }

  &__icon-box {
    // `color/azure/35-70%` do Figma.
    @apply flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-[rgba(71,85,105,0.7)] text-white;
  }

  &__title {
    @apply min-w-0 flex-1 text-[12px] font-normal leading-4 text-[#f0f2f7];
  }

  // Caixa de 14px como no Figma; a fonte menor aproxima a folha do chevron do
  // projeto (9,4px de largura) da do desenho (9,2px) — glifos diferentes, então
  // a altura fica ~0,7px mais fina.
  &__chevron {
    @apply flex size-[14px] shrink-0 items-center justify-center text-[11px] text-[#93a4d5] transition-transform duration-200;
  }

  &__header[aria-expanded='true'] &__chevron {
    @apply rotate-180;
  }

  &__body {
    @apply mt-3;
  }

  &__divider {
    @apply mb-3 border-t border-white/10;
  }

  &__doc-title {
    @apply mb-3 text-base font-bold leading-6 text-[#f8fafc];
  }

  &__section {
    @apply mb-4 last:mb-0;

    p {
      @apply mb-1 text-[12px] leading-5 text-[#e2e8f0] last:mb-0;
    }
  }

  &__section-title {
    @apply mb-1 text-base font-bold leading-6 text-[#f8fafc];
  }

  // Enumerações que já carregam o próprio marcador no texto ("I —", "a)"):
  // recuo sem marcador, pra não sair "1. a) ...".
  &__item {
    @apply pl-3;
  }

  &__subitem {
    @apply pl-6;
  }

  &__ordered {
    @apply my-1 list-decimal pl-5 text-[12px] leading-5 text-[#e2e8f0];
  }

  &__link {
    @apply underline;
  }
}
</style>
